import axios, { AxiosRequestConfig, AxiosError } from "axios";
import Q from "q";
import axiosRetry from "./requestRetry";
import { showToast, refreshToken, getToken } from "./helpers";
import { BROWSER_TYPE } from "./conf";
import { isValidKey } from "./utils";
import { v4 as uuid } from "uuid";
import shajs from "sha.js";
import JSEncrypt from "jsencrypt";
import { trackHelper } from "avatr-utils";
import { ClientInfo } from "avatr-utils/lib/clientHelper";
import { isTaskRequest, getTokenFromStorage } from "libs/task";
export interface ResponseBase<T = any> {
  code?: number;
  message?: string;
  result?: T;
}

export const REPONSE_CODE = {
  401: "用户没有权限（登录过期、token错误）",
  400: "发出的请求有错误，服务器没有进行新建或修改数据的操作。",
  403: "用户得到授权，但是访问是被禁止的。",
  404: "发出的请求针对的是不存在的记录，服务器没有进行操作。",
  406: "请求的格式不可得。",
  410: "请求的资源被永久删除，且不会再得到的。",
  422: "当创建一个对象时，发生一个验证错误。",
  500: "服务器发生错误，请检查服务器。",
  502: "网关错误。",
  503: "服务不可用，服务器暂时过载或维护。",
  504: "网关超时。",
};

const instance = axios.create({
  baseURL: "/",
  timeout: 30000,
  withCredentials: true,
});

let isRefresh = false; // 用于禁止反复拉去token

const generateSignInfo = (config: AxiosRequestConfig) => {
  const expire = Math.trunc(new Date().getTime() / 1000);
  const salt = uuid().replaceAll("-", "");
  // 使⽤RSA公钥将随机salt加密得到 x-avatr-secret
  const encryptor = new JSEncrypt(); // 创建加密对象实例
  const pubKey = process.env.REACT_APP_PUB_KEY;

  encryptor.setPublicKey(pubKey); //设置公钥
  const secret = encryptor.encrypt(salt); // 对内容进行加密

  const urlParams = []; // 默认为空数组, demo: ["test=key"]
  if (config.params && Object.keys(config.params).length > 0) {
    for (const key in config.params) {
      urlParams.push(`${key}=${encodeURIComponent(config.params[key])}`); // encodeURIComponent
    }
  }

  const submitData = config.data ? JSON.stringify(config.data) : ""; // 默认为空
  const contentType =
    config.headers["Content-Type"] ||
    (config.method.toLowerCase() === "get" ? "" : "application/json");

  const params = {
    expire,
    params: urlParams.sort(),
    request: {
      body: submitData,
      "content-type": contentType,
    },
    salt,
  };
  const sign = shajs("sha256").update(JSON.stringify(params)).digest("hex");

  const signInfo = {
    "x-avatr-sign": sign,
    "x-avatr-secret": secret,
    "x-avatr-expire": expire,
  };
  return signInfo;
};
// 微信浏览器与小程序webview共享localStorage，getToken未处理开发环境下的在微信浏览器内打开的特殊场景
const NO_TOKEN_API = ["/website/v1/msg/openLink"];

const getRiskBlackBox = () => {
  const value = window.localStorage
    ? window.localStorage.getItem("RISK_BLACKBOX")
    : "";
  return value;
};

const getOS = () => {
  switch (true) {
    case BROWSER_TYPE.IsIOS:
      return "IOS";
    case BROWSER_TYPE.IsAndroid:
      return "ANDROID";
    case BROWSER_TYPE.Weapp:
      return "MIMI_PROGRAM";
    default:
      return "";
  }
};

const getApplication = () => {
  switch (true) {
    case BROWSER_TYPE.App:
      return "AVATR_APP";
    case BROWSER_TYPE.Weapp:
      return "MINI";
    default:
      return "";
  }
};

instance.interceptors.request.use((config) => {
  let token = getToken();
  if (NO_TOKEN_API.includes(config.url)) {
    token = "";
  }
  // 任务页面 并且有刷新后的token 则用刷新后的token
  if (isTaskRequest(config.url) && getTokenFromStorage()) {
    token = getTokenFromStorage();
  }
  return {
    ...config,
    headers: {
      "Cache-Control": "no-cache",
      "login-token": token,
      "risk-black-box": getRiskBlackBox(),
      OS: getOS(),
      APPLICATION: getApplication(),
      ...config.headers,
      ...generateSignInfo(config),
    },
    // disable cache for all get req.
    ...(config.method.toLowerCase() === "get"
      ? {
          params: {
            ...config.params,
            // _t: +new Date(),
          },
        }
      : {}),
  };
});

// token 失效，刷新 token 重试
axiosRetry(instance, {
  retries: 2,
  retryDelay: (retryCount) => {
    return retryCount * 500;
  },
  retryCondition: async (error: any) => {
    if (BROWSER_TYPE.App) {
      if (error?.data?.code === 9999 && !isRefresh) {
        isRefresh = true;
        const result = await refreshToken();
        return !!result;
      }
      if (error?.data?.code === 9999 && isRefresh) {
        return true;
      }
      if (isRefresh) isRefresh = false;
    }
    return false;
  },
});

export default function request<R>(
  options: AxiosRequestConfig
): Promise<ResponseBase<R>> {
  return instance(options)
    .then((res) => {
      const data: ResponseBase<R> = res?.data || {};
      // business code.
      switch (data.code) {
        case 0: {
          return Q.resolve(data);
        }
        // case 1099: // refresh token error
        // case 10999: // visitor token can not request
        case 9999: {
          trackHelper.track("ApiError_Logic", {
            page_id: "ApiAction",
            api_url: res.config.url,
            api_error_msg: res.data.message,
            api_error_code: res.data.code,
            app_ua: ClientInfo.ClientUA,
            request_token: res.config.headers["login-token"],
          });
          if (isTaskRequest(options.url)) {
            throw new Error("");
          }
          throw new Error(data.message);
        }
        default:
          trackHelper.track("ApiError_Logic", {
            page_id: "ApiAction",
            api_url: res.config.url,
            api_error_msg: res.data.message,
            api_error_code: res.data.code,
            app_ua: ClientInfo.ClientUA,
            request_token: res.config.headers["login-token"],
          });
          throw new Error(data.message);
      }
    })
    .catch((err: AxiosError) => {
      let msg = "";

      if (err.response) {
        if (isValidKey(err.response.status, REPONSE_CODE)) {
          msg = REPONSE_CODE[err.response.status];
        }
      } else if (err.request) {
        msg = err.request.response;
      } else {
        msg = err.message;
      }
      // toast 不在任务页面才提示
      if (msg) {
        showToast({ type: "error", message: msg });
      }
      return Q.reject(msg);
    });
}
